/**
 * @module error-handler
 * @description Error handling utilities providing comprehensive error management and user notifications via Sonner toast library.
 */

import { toast } from 'sonner'
import type { HttpError, HttpErrorType } from '../types'

/**
 * Creates an HttpError with proper typing and metadata.
 * Extends native Error class with HTTP-specific properties for enhanced error handling.
 * @param type - The type of HTTP error that occurred
 * @param message - Human-readable error message
 * @param status - HTTP status code if available
 * @param statusText - HTTP status text if available
 * @param response - Raw response data from the server
 * @returns HttpError object with all metadata attached
 */
export function createHttpError(
  type: HttpErrorType,
  message: string,
  status?: number,
  statusText?: string,
  response?: unknown
): HttpError {
  // Create base Error instance and cast to HttpError
  const error = new Error(message) as HttpError
  // Set error name for identification
  error.name = 'HttpError'
  // Attach HTTP-specific metadata
  error.type = type
  error.status = status
  error.statusText = statusText
  error.response = response
  // Add timestamp for debugging
  error.timestamp = new Date().toISOString()
  return error
}

/**
 * Determines error type based on status code and error details.
 * Maps HTTP status codes to specific error types for better handling.
 * @param status - HTTP status code from response
 * @param error - Error object with additional details
 * @returns Categorized error type for specific handling
 */
export function getErrorType(status?: number, error?: unknown): HttpErrorType {
  // Check for network-level errors without status code
  if (!status) {
    // Cast to Error-like object for property access
    const errorObj = error as { name?: string } | undefined
    // Check for abort error
    if (errorObj?.name === 'AbortError') return 'abort'
    // Check for timeout error
    if (errorObj?.name === 'TimeoutError') return 'timeout'
    // Default to network error
    return 'network'
  }
  
  // Map status codes to error types
  switch (status) {
    case 400:
      return 'validation'
    case 401:
      return 'authentication'
    case 403:
      return 'authorization'
    case 404:
      return 'not_found'
    case 408:
      return 'timeout'
    case 422:
      return 'validation'
    case 429:
      return 'network'
    case 500:
    case 502:
    case 503:
    case 504:
      return 'server_error'
    default:
      // Treat 5xx as server errors, others as unknown
      return status >= 500 ? 'server_error' : 'unknown'
  }
}

/**
 * Gets user-friendly error message based on error type and details.
 * Prioritizes server-provided messages over generic fallbacks.
 * @param error - HttpError object with type and response data
 * @returns Human-readable error message for display
 */
export function getUserFriendlyMessage(error: HttpError): string {
  // Cast response to access nested properties
  const response = error.response as { error?: { message?: string }, message?: string } | undefined
  
  // Prioritize API-provided error message
  if (response?.error?.message) {
    return response.error.message
  }
  
  // Fallback to top-level message
  if (response?.message) {
    return response.message
  }
  
  // Use generic messages based on error type
  switch (error.type) {
    case 'network':
      return 'Network error. Please check your internet connection and try again.'
    case 'timeout':
      return 'Request timed out. Please try again.'
    case 'abort':
      return 'Request was cancelled.'
    case 'parse':
      return 'Unable to process server response. Please try again.'
    case 'validation':
      return 'Please check your input and try again.'
    case 'authentication':
      return 'Please sign in to continue.'
    case 'authorization':
      return 'You do not have permission to perform this action.'
    case 'not_found':
      return 'The requested resource was not found.'
    case 'server_error':
      return 'Server error. Please try again later.'
    default:
      return error.message || 'An unexpected error occurred. Please try again.'
  }
}

/**
 * Gets appropriate toast action based on error type.
 * Provides contextual actions like retry or sign-in based on error.
 * @param error - HttpError object to determine action
 * @param retryFn - Optional retry function for recoverable errors
 * @returns Action object for toast notification or undefined
 */
export function getErrorAction(error: HttpError, retryFn?: () => void): { label: string; onClick: () => void } | undefined {
  // Check if error is retryable
  const canRetry = ['network', 'timeout', 'server_error'].includes(error.type)
  
  // Provide retry action for recoverable errors
  if (canRetry && retryFn) {
    return {
      label: 'Retry',
      onClick: retryFn,
    }
  }
  
  // Provide sign-in action for auth errors
  if (error.type === 'authentication') {
    return {
      label: 'Sign In',
      onClick: () => {
        // Redirect to authentication page
        window.location.href = '/signin'
      },
    }
  }
  
  // No action available
  return undefined
}

/**
 * Shows error notification using Sonner toast library.
 * Displays error message with optional retry action and debug logging.
 * @param error - HttpError to display
 * @param options - Toast configuration options
 */
export function showErrorToast(
  error: HttpError,
  options?: {
    title?: string
    retryFn?: () => void
    duration?: number
    dismissible?: boolean
  }
): void {
  // Get user-friendly message
  const message = getUserFriendlyMessage(error)
  // Get contextual action if available
  const action = getErrorAction(error, options?.retryFn)
  
  // Display error toast notification
  toast.error(options?.title || 'Error', {
    description: message,
    duration: options?.duration || 5000,
    dismissible: options?.dismissible !== false,
    action,
    className: 'error-toast',
  })
  
  // Log detailed error in development mode
  if (process.env.NODE_ENV === 'development') {
    console.group('🚨 HTTP Error')
    console.error(`Type: ${error.type}`)
    console.error(`Status: ${error.status}`)
    console.error(`Message: ${error.message}`)
    console.error('Response:', error.response)
    console.error('Full Error:', error)
    console.groupEnd()
  }
}

/**
 * Shows success notification using Sonner toast library.
 * Displays success message with optional action button.
 * @param message - Success message to display
 * @param options - Toast configuration options
 */
export function showSuccessToast(
  message?: string,
  options?: {
    title?: string
    duration?: number
    dismissible?: boolean
    action?: {
      label: string
      onClick: () => void
    }
  }
): void {
  toast.success(options?.title || 'Success', {
    description: message || 'Operation completed successfully',
    duration: options?.duration || 3000,
    dismissible: options?.dismissible !== false,
    action: options?.action,
    className: 'success-toast',
  })
}

/**
 * Shows info notification using Sonner toast library.
 * Displays informational message for user awareness.
 * @param message - Info message to display
 * @param options - Toast configuration options
 */

/**
 * Shows warning notification using Sonner toast library.
 * Displays warning message for potential issues.
 * @param message - Warning message to display
 * @param options - Toast configuration options
 */

/**
 * Parses error response and creates HttpError.
 * Extracts error message from various response formats.
 * @param response - Fetch API Response object
 * @param responseData - Parsed response body data
 * @returns Structured HttpError object
 */
export function parseErrorResponse(
  response: Response,
  responseData?: unknown
): HttpError {
  // Extract status information
  const status = response.status
  const statusText = response.statusText
  // Determine error type from status
  const errorType = getErrorType(status, responseData)
  
  // Default error message
  let message = 'An error occurred'
  
  // Extract message from various response formats
  if (responseData) {
    // Handle string response
    if (typeof responseData === 'string') {
      message = responseData
    } else if (Array.isArray(responseData)) {
      // Handle validation error arrays (e.g. Zod validation errors)
      const firstError = responseData[0] as Record<string, unknown> | undefined
      if (firstError && typeof firstError.message === 'string') {
        message = firstError.message
      }
    } else if (typeof responseData === 'object' && responseData !== null) {
      // Cast to object for property access
      const data = responseData as Record<string, unknown>
      // Check nested error message
      if (typeof data.error === 'object' && data.error !== null) {
        const errorObj = data.error as Record<string, unknown>
        if (typeof errorObj.message === 'string') {
          message = errorObj.message
        }
      // Check top-level message
      } else if (typeof data.message === 'string') {
        message = data.message
      // Check string error property
      } else if (typeof data.error === 'string') {
        message = data.error
      }
    }
  }

  // If message looks like a stringified JSON array, try to extract the first error message
  if (message.startsWith('[')) {
    try {
      const parsed = JSON.parse(message)
      if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0].message === 'string') {
        message = parsed[0].message
      }
    } catch {
      // Not valid JSON, keep original message
    }
  }
  
  return createHttpError(errorType, message, status, statusText, responseData)
}

/**
 * Handles fetch errors and network issues.
 * Converts native Error objects to HttpError with proper typing.
 * @param error - Native Error object from fetch
 * @returns HttpError with appropriate type and message
 */
export function parseFetchError(error: Error): HttpError {
  // Check for abort error
  if (error.name === 'AbortError') {
    return createHttpError('abort', 'Request was cancelled')
  }
  
  // Check for timeout error
  if (error.name === 'TimeoutError' || error.message.includes('timeout')) {
    return createHttpError('timeout', 'Request timed out')
  }
  
  // Check for network error
  if (error.message.includes('fetch')) {
    return createHttpError('network', 'Network connection failed')
  }
  
  // Default to unknown error
  return createHttpError('unknown', error.message || 'An unexpected error occurred')
}

/**
 * Global error handler for uncaught HTTP errors.
 * Displays toast notifications for unhandled errors.
 * @param error - HttpError to handle globally
 */

/**
 * Error boundary helper for React components.
 * Provides fallback message for React error boundaries.
 * @param error - Error caught by error boundary
 * @returns User-friendly error message
 */

/**
 * Utility to check if error should trigger auto-retry.
 * Currently disabled to prevent multiple retry attempts.
 * @returns Always false as auto-retry is disabled
 */

// Removed isAuthenticationEndpoint function as auto-retry is disabled

/**
 * Calculates exponential backoff delay for retries.
 * Implements exponential backoff with jitter to prevent thundering herd.
 * @param attemptCount - Number of retry attempts made
 * @returns Delay in milliseconds before next retry
 */